import {
  Document,
  Page,
  StyleSheet,
  Font,
  View,
  Text,
  Image,
} from "@react-pdf/renderer";
import { differenceInMonths } from "date-fns";

import { Employee } from "../../features/process/types/processTypes";
import { AuthUser } from "../../features/auth/types/authType";
import { useTranslation } from "react-i18next";
import { getAmharicCalendar } from "../../utils/calendarUtility";

Font.register({
  family: "Noto",
  format: "truetype",
  fonts: [
    {
      src: "/fonts/Noto_Naskh_Arabic/NotoNaskhArabic-VariableFont_wght.ttf",
    },
    {
      src: "/fonts/Noto_Naskh_Arabic/static/NotoNaskhArabic-Regular.ttf",
    },
    {
      src: "/fonts/Noto_Naskh_Arabic/static/NotoNaskhArabic-Medium.ttf",
    },
    {
      src: "/fonts/Noto_Naskh_Arabic/static/NotoNaskhArabic-Bold.ttf",
      fontWeight: 600,
    },
  ],
});
Font.register({
  family: "Ethiopic",
  format: "truetype",
  fonts: [
    {
      src: "/fonts/Noto_Sans_Ethiopic/NotoSansEthiopic-VariableFont_wght.ttf",
    },
    {
      src: "/fonts/Noto_Sans_Ethiopic/static/NotoSansEthiopic-Regular.ttf",
    },
    {
      src: "/fonts/Noto_Sans_Ethiopic/static/NotoSansEthiopic-Medium.ttf",
      fontWeight: 400,
    },
    {
      src: "/fonts/Noto_Sans_Ethiopic/static/NotoSansEthiopic-Bold.ttf",
      fontWeight: 600,
    },
  ],
});
Font.register({
  family: "Open Sans",
  format: "truetype",
  fonts: [
    {
      // src: "https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-regular.ttf",
      src: "/fonts/open-sans-regular.ttf",
    },
    {
      // src: "https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-600.ttf",
      src: "/fonts/open-sans-600.ttf",
      fontWeight: 600,
    },
  ],
});

const arabicIntro =
  "يرغب الطرف الأول في إبرام عقد مع الطرف الثاني للعمل لديه في مهنة:[أدخل المهنة] وقد اعترف الطرفان بقدرتها على إبرام هذا العقد، واتفقا على الشروط والأحكام التالية:";
const arabicTextArrays = [
  ".1 الدولة المضيفة:",
  ".2 موقع العمل:",
  ".3 مدة العقد:",
  ".4 بدء العقد:",
  ".5 نوع المهنة:",
  ".6 الراتب الشهري:",
  ".7 طريقة الدفع: على أساس شهري، ويجب أن يتم الدفع في موعد أقصاه عشرة (10) أيام من نهاية الشهر.",
  ".8 ساعات العمل العادية: وفقاً لقانون دولة الكويت",
  ".9 أوقات الراحة: وفقاً لقانون دولة الكويت",
  ".10 العمل الإضافي: وفقاً لقانون دولة الكويت",
  "أ. العمل أكثر من العادي",
  "ب. العمل في يوم عطلة محدد والأعياد.",
  ".11 إجازة مدفوعة كاملة:",
  "أ. إجازة أسبوعية وفقاً لقانون دولة الكويت؛",
  "ب. إجازة سنوية وفقاً لقانون دولة الكويت؛",
  "ج. إجازة مرضية وفقاً لقانون دولة الكويت؛",
  "د. أخرى وفقاً لقانون دولة الكويت؛",
  ".12 تذكرة ذهاب وعودة مجانية",
  ".13 طعام مجاني، سكن مناسب أو بدل سكن.",
  ".14 خدمات طبية مجانية ومرافق بما في ذلك الأدوية.",
  ".15 تأمين الحياة الشخصية وحوادث العمل يكون على عاتق صاحب العمل.",
  ".16 في حالة وفاة الموظف خلال مدة هذا الاتفاق، سيتم إعادة جثته وممتلكاته الشخصية إلى نقطة الانطلاق. في حال عدم إمكانية إعادة الجثة، قد يتم التصرف بها بشكل مناسب بعد الحصول على موافقة مسبقة من أقرباء الموظف أو من البعثة الإثيوبية المعنية (سفارة إثيوبيا، البعثة الدائمة، القنصلية العامة، القائم بالأعمال أو القنصل العام الفخري).",
  ".17 يجب على صاحب العمل مساعدتهم في تحويل راتبه من خلال القنوات المصرفية المناسبة أو وسائل أخرى مصرح بها قانوناً.",
  ".18 إنهاء العقد: يمكن أن تشمل أسباب إنهاء عقد العمل ما يلي:",
  " أ. إنهاء من قبل صاحب العمل: يمكن لصاحب العمل إنهاء هذا العقد دون إشعار مسبق للموظف لأي من الأسباب التالية: misconduct خطير، عدم الامتثال لأوامر صاحب العمل القانونية، الإهمال المستمر للواجبات، الغياب، تسريب أسرار المؤسسة أو عندما ينتهك الموظف شروط هذا العقد.",
  " ب. إنهاء من قبل الموظف: يمكن للموظف إنهاء هذا العقد دون إشعار مسبق لصاحب العمل لأي من الأسباب التالية: إهانة خطيرة من صاحب العمل أو ممثليه، معاملة غير إنسانية وغير محتملة تعرض لها الموظف من قبل صاحب العمل أو ممثليه. يمكن للموظف إنهاء هذا العقد لأي سبب من الأسباب بعد تقديم إشعار كتابي مسبق قبل 30 يوماً لصاحب العمل.",
  ".19 التجديد: يمكن تجديد هذا العقد بالاتفاق المتبادل.",
  ".20 التعديل والتغيير: أي تعديلات أو تغييرات تتم في هذا العقد بطريقة غير مواتية للعمال دون موافقة وزارة العمل والمهارات الإثيوبية (MoLS) ستكون غير فعالة.",
  ".21 تسوية المنازعات: ستتم تسوية جميع المطالبات والشكاوى المتعلقة بعقد العمل للموظف وفقاً لسياسات الشركة وقواعدها وأنظمتها والقوانين. في حالة التسوية، يجب أن يتم الأمر بشكل ودي مع مشاركة البعثة الإثيوبية المعنية (السفارة، البعثة الدائمة، القنصلية العامة، القائم بالأعمال والقنصل العام الفخري).",
  ".22 في حال فشل التسوية الودية، سيتم تقديم الأمر إلى الهيئة الحكومية المختصة في (الدولة المضيفة) أو في إثيوبيا حسب خيار الطرف المشتكي.",
  ".23 تذكرة ذهاب وعودة مجانييجب على الموظف الالتزام بقواعد الشركة الخاصة بصاحب العمل والامتثال للقوانين السارية في الدولة المضيفة.",
  ".24 يتحمل صاحب العمل مسؤولية الحصول على تأشيرة الدخول الضرورية وتصريح العمل القانوني للموظف من الدولة المضيفة.",
  ".25 القانون المطبق: يتم تنظيم الشروط والأحكام الأخرى للتوظيف التي تتماشى مع الأحكام المذكورة أعلاه بموجب القوانين الدائمة.",
  ".26 إذا تم انتهاك هذا العقد من قبل صاحب العمل، فقد وافقت الوكالة على أن تكون مسؤولة بالتضامن.",
  ".27 تم إعداد هذا العقد من أربع (4) نسخ؛ ستحصل الوكالة وصاحب العمل والموظف على نسخة، وستبقى النسخة المتبقية في وزارة العمل والمهارات الإثيوبية (MoLS).",
];

const styles = StyleSheet.create({
  page: {
    fontSize: 10,
    paddingTop: 20,
    paddingBottom: 20,
    paddingLeft: 4,
    paddingRight: 4,
    lineHeight: 1.75,
    flexDirection: "column",
  },
  fullPage: {
    flexDirection: "column",
    flexShrink: 1,
    borderWidth: 1,
    borderColor: "black",
    borderStyle: "solid",
  },
  pageView: {
    flexDirection: "column",
    flexGrow: 1,
  },
  pageSection: {
    flexDirection: "row",
    flexGrow: 1,
  },
  pageViewLeft: {
    flexDirection: "column",
    flexGrow: 1,
    flexShrink: 1,
    width: "30%",
    paddingLeft: "4px",
    borderRightWidth: 2,
    borderColor: "#B2BEB5",
    borderStyle: "dashed",
  },
  pageViewCenter: {
    flexDirection: "column",
    flexGrow: 1,
    flexShrink: 1,
    width: "40%",
    paddingLeft: "4px",
    borderRightWidth: 2,
    borderColor: "#B2BEB5",
    borderStyle: "dashed",
  },
  pageViewRight: {
    flexDirection: "column",
    flexGrow: 1,
    flexShrink: 1,
    width: "30%",
    paddingRight: "4px",
  },
  row2: {
    flexDirection: "row",
    alignItems: "center",
    height: 30,
  },
  row21: {
    borderColor: "#000000",
    borderWidth: 2,
    alignItems: "center",
    marginRight: -1,
    borderRightWidth: 0,
  },
  row31: {
    width: "25%",
    borderColor: "#000000",
    borderWidth: 1,
    alignItems: "center",
    marginRight: -1,
    borderTopWidth: 0,
  },
  row3: {
    flexDirection: "column",
  },
  row33: {
    flexDirection: "row",
    width: "100%",
    justifyContent: "flex-start",
    alignItems: "flex-end",
  },
  row33End: {
    flexDirection: "row",
    width: "100%",
    justifyContent: "space-between",
    alignItems: "flex-end",
  },
  cellLeft: {
    textAlign: "left",
    fontWeight: "medium",
    fontFamily: "Ethiopic",
    fontSize: 14,
  },
  cellCenter: {
    textAlign: "left",
    fontWeight: "medium",
    fontFamily: "Open Sans",
    fontSize: 14,
  },
  cellRight: {
    textAlign: "right",
    fontWeight: "medium",
    fontFamily: "Noto",
    fontSize: 14,
  },
  cellCenterBold: {
    textAlign: "left",
    fontFamily: "Open Sans",
    fontSize: 16,
    flexGrow: 1,
    borderBottomWidth: 1,
    borderColor: "black",
    fontWeight: "bold",
  },
  cellCenterBold2: {
    textAlign: "left",
    fontFamily: "Open Sans",
    fontSize: 16,
    flexGrow: 1,
    fontWeight: "bold",
  },
  cellRightBold: {
    textAlign: "right",
    width: "75%",
    fontFamily: "Noto",
    fontSize: 14,
    flexGrow: 1,
    borderBottomWidth: 1,
    borderColor: "black",
    fontWeight: "bold",
  },
  headerCenter: {
    textAlign: "center",

    fontWeight: "medium",
    fontFamily: "Ethiopic",
    fontSize: "14px",
  },
  header: {
    position: "absolute",
    top: 10,
    left: 0,
    right: 0,
    height: "4px",
    marginBottom: 10,
  },
  dateHeader: {
    textAlign: "left",
    margin: 0,
    padding: 0,
    fontWeight: "medium",
    color: "#008000",
    fontFamily: "Ethiopic",
    fontSize: "14px",
    width: "25%",
  },
  header2: {
    textAlign: "center",
    margin: 0,
    padding: 0,
    fontWeight: "medium",
    color: "#008000",
    fontFamily: "Ethiopic",
    fontSize: "16px",
    borderBottomWidth: 2,
    borderColor: "black",
  },
  footer: {
    textAlign: "center",
    height: "25px",
    display: "flex",
    fontFamily: "Ethiopic",
    fontSize: "16px",
    fontWeight: "normal",
    color: "#000",
    backgroundColor: "#FFA500",
    marginTop: 20,
  },
});

// 2a668d c5dceb
export interface Props {
  employee: Employee;
  user: AuthUser;
}

export const KuwaitContract = ({ employee, user }: Props) => {
  const { t: te } = useTranslation("enums");
  const company = user && user.company ? user.company : null;
  const edu = employee.education?.educationLevel as string;
  const getEducationLevelCaps = (education: string) => {
    let educationCap = "";
    switch (education) {
      case "grade_eight_complete":
        educationCap = "Primary";
        break;
      case "secondary_complete":
        educationCap = "Secondary";
        break;
      case "vocational_complete":
        educationCap = "Vocational";
        break;
      case "college_complete":
        educationCap = "College";
        break;
      case "post_graduate":
        educationCap = "Post Graduate";
        break;
      case "other_education":
        educationCap = "Other Education";
        break;
    }
    return educationCap;
  };
  //const address = company && company.address ? company.address : null;
  const agency = employee.owner;
  const formattedDate = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const employeeProspect =
    employee && employee.prospects && employee.prospects[0]
      ? employee.prospects[0]
      : null;
  let agent = company && company.partners ? company.partners[0] : null;
  if (employeeProspect) agent = employeeProspect;
  if (
    employee &&
    employee.address &&
    employee.additionalData &&
    employee.contactPerson &&
    employee.contactPerson.address &&
    employee.visa &&
    employee.visa.address &&
    employee.passportNumber &&
    employee.passportNumber.length > 0 &&
    agency &&
    agent
  ) {
    const empFullName = employee.givenName + " " + employee.surName;
    return (
      <Document
        creator="pinnasofts"
        author="pinnaSofts"
        title="EnjazPlus"
        subject="Kuwait Contract"
      >
        <Page size="A3" style={styles.page}>
          <View style={{ borderTop: "1px solid #000" }} fixed={true}></View>
          <View style={styles.fullPage}>
            <View style={styles.pageSection}>
              <View style={styles.pageViewLeft}>
                <View key="2">
                  <Text
                    style={[
                      styles.cellLeft,
                      {
                        color: "#008000",
                        textAlign: "center",
                        fontWeight: "bold",
                        fontSize: 14,
                      },
                    ]}
                  >
                    {`የስራ ውል`}
                  </Text>
                </View>
              </View>
              <View style={styles.pageViewCenter}>
                <View key="2c">
                  <Text
                    style={[
                      styles.cellCenter,
                      {
                        color: "#008000",
                        textAlign: "center",
                        fontWeight: "bold",
                        fontSize: 14,
                      },
                    ]}
                  >
                    {`Employment Contract in the State of Kuwait`.toUpperCase()}
                  </Text>
                </View>
              </View>
              <View style={styles.pageViewRight}>
                <View key="2r">
                  <Text
                    style={[
                      styles.cellRight,
                      {
                        color: "#008000",
                        textAlign: "center",
                        fontWeight: "bold",
                        fontSize: 14,
                      },
                    ]}
                  >
                    {` عقد العمل في دولة الكويت`}
                  </Text>
                </View>
              </View>
            </View>
            <View style={styles.pageSection}>
              <View style={styles.pageViewLeft}>
                <View key="-10">
                  <Text
                    style={[styles.cellLeft]}
                  >{`ይህ የስራ ውል ከዚህ በታች በተለመለከቱት ተዋዋይ ወገኖች ተፈጽሟል፤`}</Text>
                </View>
              </View>
              <View style={styles.pageViewCenter}>
                <View key="-10c">
                  <Text style={[styles.cellCenter]}>
                    {`This employment contract shall be executed and concluded by and between:`}
                  </Text>
                </View>
              </View>
              <View style={styles.pageViewRight}>
                <View key="-10r">
                  <Text
                    style={[styles.cellRight]}
                  >{`سيتم تنفيذ هذا العقد بين:`}</Text>
                </View>
              </View>
            </View>
            <View style={styles.pageSection}>
              <View style={styles.pageViewLeft}>
                <View
                  style={{
                    marginTop: 5,
                    borderBottomWidth: 1,
                    borderColor: "black",
                    backgroundColor: "#E0E0E0",
                    paddingBottom: 2,
                  }}
                  key="-20"
                >
                  <Text
                    style={[
                      styles.cellLeft,
                      { color: "#000", fontWeight: "bold" },
                    ]}
                  >{`ሀ. አሰሪ`}</Text>
                </View>
                <View style={[styles.row3]} key="20">
                  <View style={{ lineHeight: 2.5 }}>
                    <Text style={styles.cellLeft}>{`ሙሉ ስም: `}</Text>

                    <Text style={styles.cellLeft}>{`ዜግነት: `}</Text>

                    <Text style={styles.cellLeft}>{`ፖ.ሳ.ቁ:`}</Text>
                    <Text style={styles.cellLeft}>{`ስልክ ቁጥር: `}</Text>
                  </View>
                </View>
              </View>
              <View style={styles.pageViewCenter}>
                <View
                  style={{
                    marginTop: 5,
                    borderBottomWidth: 1,
                    borderColor: "black",
                    backgroundColor: "#E0E0E0",
                    paddingBottom: 2,
                  }}
                  key="-20c"
                >
                  <Text
                    style={[
                      styles.cellCenter,
                      { color: "#000", fontWeight: "bold" },
                    ]}
                  >{`1. Employer Information`}</Text>
                </View>
                <View style={[styles.row3]} key="20c">
                  <View style={[styles.row33]}>
                    <Text style={styles.cellCenter}>{`Name: `}</Text>
                    <Text
                      style={[styles.cellCenterBold]}
                    >{`${employee.visa.fullName}`}</Text>
                  </View>
                  <View style={[styles.row33]}>
                    <Text style={styles.cellCenter}>{`Nationality: `}</Text>
                    <Text
                      style={[styles.cellCenterBold]}
                    >{`${"KUWAITI"}`}</Text>
                  </View>
                  <View style={[styles.row33]}>
                    <Text style={styles.cellCenter}>{`P.O.Box:`}</Text>
                    <Text style={styles.cellCenterBold}>{``}</Text>
                  </View>
                  <View style={[styles.row33]}>
                    <Text style={styles.cellCenter}>{`Phone Number: `}</Text>
                    <Text style={[styles.cellCenterBold]}>{`${
                      employee.visa.address.telephone
                        ? employee.visa.address.telephone
                        : ""
                    }`}</Text>
                  </View>
                </View>
              </View>
              <View style={styles.pageViewRight}>
                <View
                  style={{
                    marginTop: 5,
                    borderBottomWidth: 1,
                    borderColor: "black",
                    backgroundColor: "#E0E0E0",
                    paddingBottom: 2,
                  }}
                  key="-20r"
                >
                  <Text
                    style={[
                      styles.cellRight,
                      { color: "#000", fontWeight: "bold" },
                    ]}
                  >{`١. معلومات الطرف الأول`}</Text>
                </View>
                <View style={[styles.row3]} key="20r">
                  <View style={[styles.row33End]}>
                    <Text style={styles.cellRightBold}>
                      {`${employee.visa.fullName}`}
                    </Text>
                    <Text style={styles.cellRight}>{` اسم : `}</Text>
                  </View>
                  <View style={[styles.row33End]}>
                    <Text style={[styles.cellRightBold]}>{`${"KUWAITI"}`}</Text>
                    <Text style={styles.cellRight}>{`الجنسية: `}</Text>
                  </View>

                  <View style={[styles.row33End]}>
                    <Text style={[styles.cellRightBold]}>{`${""}`}</Text>
                    <Text style={styles.cellRight}>{`صندوق البريد: `}</Text>
                  </View>
                  <View style={[styles.row33End]}>
                    <Text style={[styles.cellRightBold]}>{`${
                      employee.visa.address.telephone
                        ? employee.visa.address.telephone
                        : ""
                    }`}</Text>
                    <Text style={styles.cellRight}>{`رقم الهاتف: `}</Text>
                  </View>
                </View>
              </View>
            </View>
            <View style={styles.pageSection}>
              <View style={styles.pageViewLeft}>
                <View
                  style={{
                    paddingBottom: 2,
                    backgroundColor: "#E0E0E0",
                    paddingHorizontal: 3,
                  }}
                  key="30"
                >
                  <Text
                    style={[
                      styles.cellLeft,
                      { color: "#000", fontWeight: "bold" },
                    ]}
                  >{`ለ: ሠራተኛ`}</Text>
                </View>
                <View style={[styles.row3, { lineHeight: 2.5 }]} key="40">
                  <Text style={styles.cellLeft}>{`ሙሉ ስም: `}</Text>

                  <Text style={styles.cellLeft}>{`ዜግነት: `}</Text>
                  <Text style={styles.cellLeft}>{`እድሜ: `}</Text>
                  <Text style={styles.cellLeft}>{`ጾታ: `}</Text>
                  <Text style={styles.cellLeft}>{`የት/ት ደረጃ: `}</Text>

                  <Text style={styles.cellLeft}>{`የጋብቻ ሁኔታ: `}</Text>
                  <Text style={styles.cellLeft}>{`ፓስፖርት ቁጥር: `}</Text>
                </View>
              </View>
              <View style={styles.pageViewCenter}>
                <View
                  style={{
                    paddingBottom: 2,
                    backgroundColor: "#E0E0E0",
                    paddingHorizontal: 3,
                  }}
                  key="30"
                >
                  <Text
                    style={[
                      styles.cellCenter,
                      { color: "#000", fontWeight: "bold" },
                    ]}
                  >{`2: Employee Information`}</Text>
                </View>
                <View style={[styles.row3]} key="40">
                  <View style={[styles.row33]}>
                    <Text style={styles.cellCenter}>{`Name: `}</Text>
                    <Text
                      style={[styles.cellCenterBold]}
                    >{`${empFullName}`}</Text>
                  </View>

                  <View style={[styles.row33]}>
                    <Text style={styles.cellCenter}>{`Nationality: `}</Text>
                    <Text
                      style={[styles.cellCenterBold]}
                    >{`${employee.additionalData.nationality?.toUpperCase()}`}</Text>
                  </View>
                  <View style={[styles.row33]}>
                    <Text style={styles.cellCenter}>{`Age: `}</Text>
                    <Text style={[styles.cellCenterBold]}>{`${Math.floor(
                      differenceInMonths(
                        new Date(),
                        new Date(employee.dateOfBirth as Date)
                      ) / 12
                    ).toLocaleString("en-US")}`}</Text>
                  </View>
                  <View style={[styles.row33]}>
                    <Text style={styles.cellCenter}>{`Sex: `}</Text>
                    <Text style={[styles.cellCenterBold]}>{`${
                      employee.sex && employee.sex.toUpperCase()
                    }`}</Text>
                  </View>
                  <View style={[styles.row33]}>
                    <Text
                      style={styles.cellCenter}
                    >{`Educational level: `}</Text>
                    <Text
                      style={[styles.cellCenterBold]}
                    >{`${getEducationLevelCaps(edu).toUpperCase()}`}</Text>
                  </View>

                  <View style={[styles.row33]}>
                    <Text style={styles.cellCenter}>{`Marital Status: `}</Text>
                    <Text
                      style={[styles.cellCenterBold]}
                    >{`${employee.additionalData.maritalStatus?.toUpperCase()}`}</Text>
                  </View>
                  <View style={[styles.row33]}>
                    <Text style={styles.cellCenter}>{`Passport Number: `}</Text>
                    <Text
                      style={[styles.cellCenterBold]}
                    >{`${employee.passportNumber}`}</Text>
                  </View>
                </View>
              </View>
              <View style={styles.pageViewRight}>
                <View
                  style={{
                    paddingBottom: 2,
                    backgroundColor: "#E0E0E0",
                    paddingHorizontal: 3,
                  }}
                  key="30"
                >
                  <Text
                    style={[
                      styles.cellRight,
                      { color: "#000", fontWeight: "bold" },
                    ]}
                  >{` ٢. معلومات الموظف`}</Text>
                </View>
                <View style={[styles.row3]} key="40">
                  <View style={[styles.row33End]}>
                    <Text style={styles.cellRightBold}>{`${empFullName}`}</Text>
                    <Text style={styles.cellRight}>{`اسم: `}</Text>
                  </View>
                  <View style={[styles.row33End]}>
                    <Text style={styles.cellRightBold}>
                      {`${employee.additionalData.nationality?.toUpperCase()}`}
                    </Text>
                    <Text style={styles.cellRight}>{`الجنسية: `}</Text>
                  </View>
                  <View style={[styles.row33End]}>
                    <Text style={styles.cellRightBold}>
                      {`${Math.floor(
                        differenceInMonths(
                          new Date(),
                          new Date(employee.dateOfBirth as Date)
                        ) / 12
                      ).toLocaleString("en-US")}`}
                    </Text>
                    <Text style={styles.cellRight}>{`العمر: `}</Text>
                  </View>
                  <View style={[styles.row33End]}>
                    <Text style={styles.cellRightBold}>
                      {`${employee.sex && employee.sex.toUpperCase()}`}
                    </Text>
                    <Text style={styles.cellRight}>{`الجنس: `}</Text>
                  </View>
                  <View style={[styles.row33End]}>
                    <Text style={styles.cellRightBold}>
                      {`${getEducationLevelCaps(edu).toUpperCase()}`}
                    </Text>
                    <Text style={styles.cellRight}>{`المستوى التعليمي: `}</Text>
                  </View>
                  <View style={[styles.row33End]}>
                    <Text style={styles.cellRightBold}>
                      {`${employee.additionalData.maritalStatus?.toUpperCase()}`}
                    </Text>
                    <Text style={styles.cellRight}>{`الحالة المهنية: `}</Text>
                  </View>
                  <View style={[styles.row33End]}>
                    <Text style={styles.cellRightBold}>
                      {`${employee.passportNumber}`}
                    </Text>
                    <Text style={styles.cellRight}>{`رقم الجواز: `}</Text>
                  </View>
                </View>
              </View>
            </View>
            <View style={styles.pageSection}>
              <View style={styles.pageViewLeft}>
                <View style={[styles.row3]} key="44">
                  <View
                    style={{
                      backgroundColor: "#E0E0E0",
                      paddingBottom: 2,
                    }}
                    key="30"
                  >
                    <Text
                      style={[
                        styles.cellLeft,
                        { color: "#000", fontWeight: "bold" },
                      ]}
                    >{`በአደጋ ጊዜ የቅርብ ተጠሪ`}</Text>
                  </View>
                  <View style={[styles.row3, { lineHeight: 2.5 }]} key="44cp">
                    <Text style={styles.cellLeft}>{`ሙሉ ስም: `}</Text>
                    <Text style={styles.cellLeft}>{`አድራሻ: `}</Text>
                    <Text style={styles.cellLeft}>{`ፖ.ሳ.ቁ:`}</Text>
                    <Text style={styles.cellLeft}>{`ስልክ ቁጥር: `}</Text>
                  </View>
                </View>
              </View>
              <View style={styles.pageViewCenter}>
                <View style={[styles.row3]} key="44">
                  <View
                    style={{
                      backgroundColor: "#E0E0E0",
                      paddingBottom: 2,
                    }}
                    key="30"
                  >
                    <Text
                      style={[
                        styles.cellCenter,
                        { color: "#000", fontWeight: "bold" },
                      ]}
                    >{`Emergency Contact`}</Text>
                  </View>

                  <View style={[styles.row33]}>
                    <Text style={styles.cellCenter}>{`Name: `}</Text>
                    <Text style={styles.cellCenterBold}>
                      {`${employee.contactPerson.displayName || ""}`}
                    </Text>
                  </View>

                  <View style={[styles.row33]}>
                    <Text style={styles.cellCenter}>{`Address: `}</Text>
                    <Text style={styles.cellCenterBold}>
                      {`${employee.contactPerson.address.city || ""}`}
                    </Text>
                  </View>
                  <View style={[styles.row33]}>
                    <Text style={styles.cellCenter}>{`P.O.Box:`}</Text>
                    <Text style={styles.cellCenterBold}>{``}</Text>
                  </View>
                  <View style={[styles.row33]}>
                    <Text style={styles.cellCenter}>{`Phone Number: `}</Text>
                    <Text style={styles.cellCenterBold}>{`${
                      employee.contactPerson.address.telephone || ""
                    }`}</Text>
                  </View>
                </View>
              </View>
              <View style={styles.pageViewRight}>
                <View style={[styles.row3]} key="44">
                  <View
                    style={{
                      backgroundColor: "#E0E0E0",
                      paddingBottom: 2,
                    }}
                    key="30"
                  >
                    <Text
                      style={[
                        styles.cellRight,
                        { color: "#000", fontWeight: "bold" },
                      ]}
                    >{`الاتصال الطارئ`}</Text>
                  </View>

                  <View style={[styles.row33End]}>
                    <Text style={styles.cellRightBold}>
                      {`${employee.contactPerson.displayName || ""}`}
                    </Text>
                    <Text style={styles.cellRight}>{`اسم: `}</Text>
                  </View>
                  <View style={[styles.row33End]}>
                    <Text style={styles.cellRightBold}>
                      {`${employee.contactPerson.address.city || ""}`}
                    </Text>
                    <Text style={styles.cellRight}>{`العنوان: `}</Text>
                  </View>

                  <View style={[styles.row33End]}>
                    <Text style={styles.cellRightBold}>{``}</Text>
                    <Text style={styles.cellRight}>{`صندوق البريد: `}</Text>
                  </View>
                  <View style={[styles.row33End]}>
                    <Text style={styles.cellRightBold}>
                      {`${employee.contactPerson.address.telephone || ""}`}
                    </Text>
                    <Text style={styles.cellRight}>{`الهاتف: `}</Text>
                  </View>
                </View>
              </View>
            </View>

            <View style={styles.pageSection}>
              <View style={styles.pageViewLeft}>
                <View style={[styles.row3]} key="44">
                  <View
                    style={{
                      backgroundColor: "#E0E0E0",
                      paddingBottom: 2,
                    }}
                    key="30"
                  >
                    <Text
                      style={[
                        styles.cellLeft,
                        {
                          textAlign: "center",
                          color: "#000",
                          fontSize: 14,
                          fontWeight: "bold",
                        },
                      ]}
                    >{`መግቢያ`}</Text>
                  </View>
                  <Text
                    style={styles.cellLeft}
                  >{`አሰሪ ከሰራተኛው ጋር በ${employee.additionalData.appliedProfession?.toUpperCase()} ሙያ እንዲሰራለት ይህን የስራ ውል ተስማምተዋል። ሁለቱም አካላት በሚከተሉት የውል ግዴታዎች እና ሁኔታዎች ለመገዛት ፈቅደው ተስማምተዋል፤`}</Text>
                </View>
              </View>
              <View style={styles.pageViewCenter}>
                <View style={[styles.row3]} key="44">
                  <View
                    style={{
                      backgroundColor: "#E0E0E0",
                      paddingBottom: 2,
                    }}
                    key="30"
                  >
                    <Text
                      style={[
                        styles.cellCenter,
                        {
                          textAlign: "center",
                          color: "#000",
                          fontSize: 14,
                          fontWeight: "bold",
                        },
                      ]}
                    >{`Preamble`}</Text>
                  </View>
                  <View style={{ flexDirection: "row" }}>
                    <Text style={styles.cellCenter}>
                      {`The first party wishes to conclude a contract with the second party to work for it in the profession of `}
                      <Text
                        style={styles.cellCenterBold2}
                      >{`${employee.additionalData.appliedProfession?.toUpperCase()}`}</Text>

                      <Text>{`. The parties acknowledged their capacity to conclude this contract, they agreed upon the following terms and conditions:`}</Text>
                    </Text>
                  </View>
                </View>
              </View>
              <View style={styles.pageViewRight}>
                <View style={[styles.row3]} key="44">
                  <View
                    style={{
                      backgroundColor: "#E0E0E0",
                      paddingBottom: 2,
                    }}
                    key="30"
                  >
                    <Text
                      style={[
                        styles.cellRight,
                        {
                          textAlign: "center",
                          color: "#000",
                          fontSize: 14,
                          fontWeight: "bold",
                        },
                      ]}
                    >{`مقدمة`}</Text>
                  </View>
                  <View
                    key={"1212kjkjlkj"}
                    style={[
                      {
                        paddingHorizontal: 2,
                        flexDirection: "row-reverse",
                        flexWrap: "wrap",
                      },
                    ]}
                  >
                    {arabicIntro.split(" ").map((word, idx) => {
                      return (
                        word.length > 0 && (
                          <Text
                            style={[
                              styles.cellRight,
                              { fontWeight: "medium", marginLeft: "1pt" },
                            ]}
                            key={idx}
                          >
                            {word}
                          </Text>
                        )
                      );
                    })}
                  </View>
                </View>
              </View>
            </View>

            <View style={styles.pageSection}>
              <View style={styles.pageViewLeft}>
                <View style={[styles.row3]} key="44">
                  <Text style={styles.cellLeft}>{`1. ተቀባይ ሀገር: `}</Text>
                  <Text style={styles.cellLeft}>{`2. የስራው ስፍራ: `}</Text>
                  <Text
                    style={styles.cellLeft}
                  >{`3. የስራው ውል የሚቆይበት ጊዜ: ሁለት ዓመታት ሰራተኛው ለስራ ወደሚሰማራበት ስፍራ ለመሄድ  ከኢትዮጵያ ከሚነሳበት ጊዜ አንስቶ`}</Text>
                  <Text style={styles.cellLeft}>{`4. የሙያው አይነት: `}</Text>
                  <Text style={styles.cellLeft}>{`5. የወር ደሞዝ: `}</Text>
                  <Text
                    style={styles.cellLeft}
                  >{`6. የክፍያው አፈጻጸም፡ በየወሩ (ወሩ ካለቀበት ጊዜ አንስቶ እስከ 10 ቀናት ድረስ)`}</Text>
                  <Text
                    style={styles.cellLeft}
                  >{`7. መደበኛ የስራ ሰዓት፡ በኩዌት ህግ መሰረት`}</Text>
                  <Text
                    style={styles.cellLeft}
                  >{`8. የእረፍት ሰዓት፡ በኩዌት ህግ መሰረት`}</Text>
                  <Text
                    style={styles.cellLeft}
                  >{`9. የትርፍ ሰዓት የስራ ክፍያ፡ በኩዌት ህግ መሰረት`}</Text>
                  <Text
                    style={styles.cellLeft}
                  >{` ሀ. ከመደበኛው የስራ ሰዓት በላይ ለሚሰራ ስራ`}</Text>
                  <Text
                    style={styles.cellLeft}
                  >{` ለ. በተወሰነ የእረፍት እና የበዓል ቀናት ለሚሰራ ስራ`}</Text>
                  <Text style={styles.cellLeft}>{`10.እረፍት ከሙሉ ክፍያ ጋር፡ `}</Text>
                  <Text
                    style={styles.cellLeft}
                  >{` ሀ. የሳምንት እረፍት፡ በኩዌት ህግ መሰረት`}</Text>
                  <Text
                    style={styles.cellLeft}
                  >{` ለ. የአመት ፍቃድ፡ በኩዌት ህግ መሰረት`}</Text>
                  <Text
                    style={styles.cellLeft}
                  >{` ሐ. የህመም ፍቃድ፡ በኩዌት ህግ መሰረት`}</Text>
                  <Text
                    style={styles.cellLeft}
                  >{` መ. ሌሎች፡ በኩዌት ህግ መሰረት `}</Text>
                  <Text style={styles.cellLeft}>{`11.ነጻ የደርሶ መልስ ትኬት `}</Text>
                  <Text
                    style={styles.cellLeft}
                  >{`12. በቂ የተመጣጠነ ነጻ ምግብ እና ምቹ የመኖሪያ ቤት ወይም ማካካሻ አበል `}</Text>
                  <Text
                    style={styles.cellLeft}
                  >{`13. ነጻ የህክምና አገልግሎት ከመድሀኒት አቅርቦት ጭምር `}</Text>
                  <Text
                    style={styles.cellLeft}
                  >{`14. በአሰሪው የተገባ የሰራተኛው የግል የህይወት እና የአደጋ ኢንሹራንስ`}</Text>
                  <Text
                    style={styles.cellLeft}
                  >{`15. በዚህ የስራ ውል ቆይታ ጊዜ ውስጥ ሰራተኛው ቢሞት አስክሬኑ እና የግል ንብረቶቹ በአሰሪው ወጪ ወደ ኢትዮጵያ መመለስ ይኖርበታል። ምናልባት አስክሬኑን ወደ ኢትዮጵያ መላክ የማይችል ሆኖ ሲገኝ የሟችን የቅርብ ዘመድ እና/ወይም የኢትዮጵያ ሚሲዮን (ኤምባሲ፣ ቋሚ መልክተኛ ጽ/ቤት ጉዳይ አስፈጻሚ ወይም የክብር ቆንስላ) በሚያደርጉት የቅድሚያ ስምምነት መሰረት ተገቢውን ማድረግ ይቻላል። `}</Text>
                  <Text
                    style={styles.cellLeft}
                  >{`16. ሰራተኛው አግባብ ባለው የባንክ አሰራር ወይም ህጋዊ በሆነ በሌላ መንገድ በመጠቀም ደመዎዙን ወደ አገሩ መላክ እንዲችል አሰሪው መርዳት ይኖርበታል። `}</Text>
                  <Text
                    style={styles.cellLeft}
                  >{`17. የሰራ ዉል መቋረጥ የስራ ዉሉ የሚቋረጥበት ምክንያቶች የሚከተሉተን ሊጨምር ይችላል፤`}</Text>
                  <Text
                    style={styles.cellLeft}
                  >{` ሀ. በአሰሪዉ የሚደረግ የስራ ዉል መቋረጥ፡ አሰሪዉ አንዳችም ማስጠንቀቂ ለሰራተኛዉ ሳሰጥ ይህንን የስራ ዉል በሚከተሉት በቂ ምክንያቶች ሊቋረጥ ይችላል፤በሰራተኛዉ ጉልህ የስነ ምግባር ብልሹነት የአሰሪዉን ህጋዊ ትዕዛዝ ሆነ ብሎ ያለመወጣት ልምድ ያለበቂ ምክንያት በተደጋጋሚ ከስራ የመቅረት ፡፡የድርጅቱን ሚስጥሮች የመግለጽ የመሳሰሉት ወይም  ይህን የስራ ዉል ሲሰጥ።`}</Text>
                  <Text
                    style={styles.cellLeft}
                  >{` ለ. በሰራተኛዉ የሚደርግ የስራ ዉል መቋረጥ፡ ሰራተኛዉ አንዳችም ማስጠንቀቂያ ለአሰሪዉ ሳይሰጥ ይህንን የስራ ዉል በሚከተሉት ምክንቶች ሊያቋርጥ ይችላል፤ የአሰሪዉ ወይም የወኪሉ ከባድ ስድብ አሰሪዉ ወይም ወኪሉ ለሰራተኛዉ የሚያደርገዉ ኢሰብአዊነትና አግባብነት የሌለዉ አያያዝ፣ የአሰሪዉ ወይም የወኪሉን በሰራተኛዉ ላይ የወንጀል ድርጊት መፈጸም ወይም የወኪሎች ህጎችን የዚህን የስራ ዉል መጣስ። ሰራተኛዉ የ30 ቀን የቅድሚ የጹሁፍ ማስጠንቀቂያ ለአሰሪዉ በመስጠት ይህንን የስራ ዉል በማነኛቸዉም ምክንያት ማቋረጥ ይችላል።`}</Text>
                  <Text
                    style={styles.cellLeft}
                  >{`18. እድሳት፡ ይህ የስራ ዉል በጋራ ስምምነት ሊታደስ ይችላል።`}</Text>
                  <Text
                    style={styles.cellLeft}
                  >{`19. ማሻሻል እና መለወጥ፡ በዚህ የስራ ዉል ላይ ሊደረግ የሰራተኛዉን ጥቅም በሚቀንስ ማሻሻያ ለዉጥ እና የመሳሰሉት፤ የሥራና ክህሎት ሚኒስቴር ፈቃድ ከሌለዉ በቀር ዉጤት አይኖረዉም።`}</Text>
                  <Text
                    style={styles.cellLeft}
                  >{`20. የስራ ክርክር/ግጭት አፈታት፡ ማንኛዉም ከስራ ዉል ጋር የተዛመደ የይገባኛል ጥያቄ እና ቅሬታ በኩባንያው ፖሊሲ፣መመሪያ፣ ደንብ እና ህጎች መሰረት ይፈታል። ይህ ሳይሳካ በቀረ ጊዜ አግባብ ያለዉ የኢትዮጵያ ሚሲዮን (የኢትዮጵያ ኤምባሲ ቋሚ መልክተኛ ጽ/ቤት፣ ጉዳይ አስፈፃሚ ወይም የክብር ቆንጽላ) ተሳታፊ በሆነበት ጉዳዩ በስምምነት እነዲፈታ ጥረት ያደርጋል። `}</Text>
                  <Text
                    style={styles.cellLeft}
                  >{`21. ጥረቱ ካልተሳካም ጉዳዩ ቅሬታ ባለዉ ወገን ምርጫ በተቀባይ አገር ወይም በኢትዮጵያ ለሚገኘዉ ባለስልጣን ወይም አግባብ ለሆነ የመንግሰት አካል እንዲቀርብ ይደረጋል።`}</Text>
                  <Text
                    style={styles.cellLeft}
                  >{`22. ሰራተኛዉ የአሰሪዉን ኩባንያ መመሪያ ማክበር እና አግባብ ላለዉ የተቀባዩ አገር ህጎች መገዛት ይኖርበታል።`}</Text>
                  <Text
                    style={styles.cellLeft}
                  >{`23. አሰሪዉ ለሰራተኛዉ አስፈላጊዉን የመግቢያ ቪዛ እና ህጋዊ የስራ ፍቃድ ከተቀባዪ አገር የማግኘት ሀላፊነት አለበት።`}</Text>
                  <Text
                    style={styles.cellLeft}
                  >{`24. ተፈጻሚነት ያለዉ ህግ፡ ከላይ ከተመለከቱት አንቀጾች ጋር የሚጣጣሙ ሌሎች ግዴታዎችና የስራ ሁኔታዎች አግባብ ባላቸዉ ህጎች ተፈጻሚነት ይኖራቸዋል።`}</Text>
                  <Text
                    style={styles.cellLeft}
                  >{`25. አሰሪዉ ይህንን የስራ ዉል ቢጥስ በጋራ እና በተናጠል ተጠያቂ ለመሆን ተስማምቷል።`}</Text>
                  <Text
                    style={styles.cellLeft}
                  >{`26. ይህ የሰራ ውል በአራት ቅጂ ተዘጋጅቶ፤ ኤጀንሲዉ፣ አሰሪዉ እና ሰራተኛዉ አንድ አንድ ይደርሳቸዋል። ቀሪው አንድ ኮፒ በስራ እና ክህሎት ሚኒስቴር እንዲቀመጥ ይደረጋል።`}</Text>
                </View>
              </View>
              <View style={styles.pageViewCenter}>
                <View style={[styles.row3]} key="44">
                  <View style={styles.row33}>
                    <Text style={styles.cellCenter}>{`1. Host Contry: `}</Text>
                    <Text
                      style={styles.cellCenterBold2}
                    >{` ${employee.additionalData.appliedCountry?.toUpperCase()}`}</Text>
                  </View>
                  <View style={styles.row33}>
                    <Text
                      style={styles.cellCenter}
                    >{`2. Employment Site:`}</Text>
                    <Text style={styles.cellCenterBold2}>{` ${(
                      employee.visa.address.city || "Kuwait City"
                    ).toUpperCase()}`}</Text>
                  </View>
                  <View
                    style={{
                      flexDirection: "row",
                      maxLines: 3,
                    }}
                  >
                    <Text style={styles.cellCenter}>
                      {`3. Duration of the contract: `}

                      <Text style={styles.cellCenterBold2}>{`Two years `}</Text>
                      <Text
                        style={styles.cellCenter}
                      >{`starting from Employee’s departure from Ethiopia to the employment site`}</Text>
                    </Text>
                  </View>

                  <View style={{ flexDirection: "row" }}>
                    <Text
                      style={styles.cellCenter}
                    >{`4. Profession type: `}</Text>
                    <Text
                      style={styles.cellCenterBold2}
                    >{`${employee.additionalData.appliedProfession?.toUpperCase()}`}</Text>
                  </View>
                  <View style={{ flexDirection: "row" }}>
                    <Text
                      style={styles.cellCenter}
                    >{`5. Monthly salary: `}</Text>
                    <Text
                      style={styles.cellCenterBold2}
                    >{`${employee.additionalData.salary} KWD`}</Text>
                  </View>

                  <Text
                    style={styles.cellCenter}
                  >{`6. Payment modality: on a monthly basis and the payment shall be no later than ten (10) days from the end of the month. `}</Text>
                  <Text
                    style={styles.cellCenter}
                  >{`7. Normal working hours: as per the state of Kuwait’s law`}</Text>
                  <Text
                    style={styles.cellCenter}
                  >{`8. Rest time: as per the State of Kuwait’s law`}</Text>
                  <Text
                    style={styles.cellCenter}
                  >{`9. Overtime: as per the State of Kuwait’s law`}</Text>
                  <Text
                    style={styles.cellCenter}
                  >{` a. To work more than normal`}</Text>
                  <Text
                    style={styles.cellCenter}
                  >{` b. To work on a specific day off and holidays.`}</Text>
                  <Text
                    style={styles.cellCenter}
                  >{`10. Full Payment Leave: `}</Text>
                  <Text
                    style={styles.cellCenter}
                  >{` a. Weekly leave as per the State of Kuwait’s law;`}</Text>
                  <Text
                    style={styles.cellCenter}
                  >{` b. Annual vacation as per the State of Kuwait’s law;`}</Text>
                  <Text
                    style={styles.cellCenter}
                  >{` c. Sick leave as per the State of Kuwait’s law; and`}</Text>
                  <Text
                    style={styles.cellCenter}
                  >{` d. Others as per the State of Kuwait’s law.`}</Text>
                  <Text
                    style={styles.cellCenter}
                  >{`11. Free round-trip ticket `}</Text>
                  <Text
                    style={styles.cellCenter}
                  >{`12. Free, Adequate and balanced Food, housing allowance or compensation allowance. `}</Text>
                  <Text
                    style={styles.cellCenter}
                  >{`13. Free medical services and facilities including medicine.  `}</Text>
                  <Text
                    style={styles.cellCenter}
                  >{`14. Personal life and accident insurances covered by the Employer.`}</Text>
                  <Text
                    style={styles.cellCenter}
                  >{`15. In the event of death of the Employee during the terms of this agreement, his remains and personal belongings shall be repatriated to the point of origin. In case the repatriation of remains is not possible, the same may be disposed of as appropriate upon prior approval of the Employee’s next of kin and/or by the appropriate Ethiopian mission (Ethiopian Embassy permanent mission general consulate office charge de affairs or honorary consulate general).`}</Text>
                  <Text
                    style={styles.cellCenter}
                  >{`16. The Employer shall assist them in remitting his salary through the proper banking channel or other means authorized by law.`}</Text>
                  <Text
                    style={styles.cellCenter}
                  >{`17. Termination: grounds for termination of the employment contract may include the following:`}</Text>
                  <Text
                    style={styles.cellCenter}
                  >{` a. Termination by the Employer: the Employer may terminate this contract without serving any notice to the Employee for any of the following just cases; serious misconduct, disobedience of Employer’s lawful orders, habitual neglect of duties, absenteeism, reveling secrets of establishment or when the Employee violates terms of this contract of agreement. `}</Text>
                  <Text
                    style={styles.cellCenter}
                  >{` b. Termination by the Employee: the Employee may terminate this contract without serving any notice to the Employer for any of the following just cases; serious insult by the Employer or his representatives, inhuman and unbearable treatment accorded to the Employee by the Employer or his representative. The Employee may terminate this contract for any reason by serving 30 days advance written notice to the Employer.`}</Text>
                  <Text
                    style={styles.cellCenter}
                  >{`18. Renewal: 	this contract may be renewed by the mutual agreement. `}</Text>
                  <Text
                    style={styles.cellCenter}
                  >{`19. Modification and alteration: any modification and alternations and the like made in this contract unfavorable to the worker without the consent of the Ethiopian Ministry of Labor and Skills (MoLS) shall not have any effect.`}</Text>
                  <Text
                    style={styles.cellCenter}
                  >{`20. Settlement of disputes: all claims and complaints related to the employment contract of the Employee shall be settled in accordance with the companies’ policies, rules, regulations, and laws. In case of settlement, the matter shall be settled amicably with the involvement of the appropriate Ethiopian Mission (Embassy, permanent Mission, General Consulate Office Charge de affairs or honorary Consulate General). `}</Text>
                  <Text
                    style={styles.cellCenter}
                  >{`21. In case the amicable settlement fails, the matter shall be submitted to the competent or appropriate government body in (host country) or in Ethiopia at the option of the complaining party`}</Text>
                  <Text
                    style={styles.cellCenter}
                  >{`22. The Employee shall observe Employer's company rules and abide by the pertinent laws of the host country.`}</Text>
                  <Text
                    style={styles.cellCenter}
                  >{`23. The Employer is responsible to obtain the necessary entry visa and legal work permit for the employee form the host country.`}</Text>
                  <Text
                    style={styles.cellCenter}
                  >{`24. Applicable law: Other terms and conditions of employment which are consistent with the above provision shall be governed by the permanent laws`}</Text>
                  <Text
                    style={styles.cellCenter}
                  >{`25. If this contract of employment is violated by Employer, the agency has agreed to be held responsible jointly and severally.`}</Text>
                  <Text
                    style={styles.cellCenter}
                  >{`26. This contract of employment is drawn in four (4) copies; the Agency, the Employer, and the Employee will be provided one each and the remaining one copy will remain at the Ethiopian Ministry of Labor and Skills (MoLS).`}</Text>
                </View>
              </View>
              <View style={styles.pageViewRight}>
                <View style={[styles.row3]} key="44">
                  {arabicTextArrays.map((text, idx) => {
                    return (
                      <View
                        key={idx}
                        style={[
                          {
                            paddingHorizontal: 2,
                            flexDirection: "row-reverse",
                            flexWrap: "wrap",
                          },
                        ]}
                      >
                        {text.split(" ").map((word, idx) => {
                          return (
                            word.length > 0 && (
                              <Text
                                style={[
                                  styles.cellRight,
                                  { fontWeight: "medium", marginLeft: "1pt" },
                                ]}
                                key={idx}
                              >
                                {word}
                              </Text>
                            )
                          );
                        })}
                      </View>
                    );
                  })}
                </View>
              </View>
            </View>
          </View>
          <View
            style={[
              styles.pageSection,
              {
                flexGrow: 0,
                borderWidth: 2,
                borderColor: "#000",
                borderStyle: "solid",
              },
            ]}
            break={true}
          >
            <View
              style={[
                styles.pageViewLeft,
                {
                  width: "33%",
                  flexGrow: 0,
                  paddingVertical: 5,
                },
              ]}
            >
              <Text
                style={[styles.cellLeft, { fontSize: 12, fontWeight: "bold" }]}
              >{`ይህን የስራ ዉል በ${getAmharicCalendar(
                new Date(formattedDate),
                true
              )} ተፈራርመናል፡፡`}</Text>
            </View>
            <View
              style={[
                styles.pageViewCenter,
                {
                  width: "33%",
                  flexGrow: 0,
                  paddingVertical: 5,
                },
              ]}
            >
              <Text
                style={[
                  styles.cellCenter,
                  { fontSize: 12, fontWeight: "bold" },
                ]}
              >{`We hereby sign this contract as of ${formattedDate}.`}</Text>
            </View>
            <View
              style={[
                styles.pageViewRight,
                {
                  width: "33%",
                  flexGrow: 0,
                  paddingVertical: 5,
                },
              ]}
            >
              <Text
                style={[styles.cellRight, { fontSize: 12, fontWeight: "bold" }]}
              >{`نحن نوقع هذا العقد بتاريخ ${formattedDate}.`}</Text>
            </View>
          </View>

          <View
            style={[
              styles.pageSection,
              {
                marginTop: 20,
                flexGrow: 0,
                borderWidth: 2,
                borderColor: "#000",
                borderStyle: "solid",
              },
            ]}
          >
            <View
              style={[
                styles.pageViewLeft,
                {
                  width: "33%",
                  flexGrow: 0,
                  backgroundColor: "#E0E0E0",
                  lineHeight: 1,
                  paddingBottom: 5,
                },
              ]}
            >
              <Text
                style={[
                  styles.cellLeft,
                  { fontSize: 10, textAlign: "center", fontWeight: "bold" },
                ]}
              >{`የግል ስራና ሰራተኛ አገናኝ ኤጀንሲ`}</Text>
              <Text
                style={[
                  styles.cellCenter,
                  { fontSize: 10, textAlign: "center", fontWeight: "bold" },
                ]}
              >{`Private Employment Agency`}</Text>
              <Text
                style={[
                  styles.cellRight,
                  { fontSize: 10, textAlign: "center", fontWeight: "bold" },
                ]}
              >{`الوكالة الخاصة للتوظيف`}</Text>
            </View>
            <View
              style={[
                styles.pageViewLeft,
                {
                  borderRightWidth: 0,
                  width: "67%",
                  flexGrow: 0,
                  textAlign: "center",
                  justifyContent: "center",
                  alignItems: "center",
                },
              ]}
            >
              <Text
                style={[
                  styles.cellLeft,
                  {
                    fontSize: 14,
                    fontWeight: "bold",
                    textAlign: "center",
                    justifyContent: "center",
                    alignItems: "center",
                  },
                ]}
              >
                {agency.displayName.toUpperCase()}
              </Text>
            </View>
          </View>
          <View
            style={[
              styles.pageSection,
              {
                flexGrow: 0,
                borderWidth: 2,
                borderColor: "#000",
                borderStyle: "solid",
              },
            ]}
          >
            <View
              style={[
                styles.pageViewLeft,
                {
                  width: "33%",
                  flexGrow: 0,
                  backgroundColor: "#E0E0E0",
                  lineHeight: 1,
                  paddingBottom: 5,
                },
              ]}
            >
              <Text
                style={[
                  styles.cellLeft,
                  { fontSize: 10, textAlign: "center", fontWeight: "bold" },
                ]}
              >{`የኤጀንሲው ተወካይ`}</Text>
              <Text
                style={[
                  styles.cellCenter,
                  { fontSize: 10, textAlign: "center", fontWeight: "bold" },
                ]}
              >{`Agency representative`}</Text>
              <Text
                style={[
                  styles.cellRight,
                  { fontSize: 10, textAlign: "center", fontWeight: "bold" },
                ]}
              >{`ممثل الوكالة`}</Text>
            </View>
            <View
              style={[
                styles.pageViewLeft,
                {
                  borderRightWidth: 0,
                  width: "67%",
                  flexGrow: 0,
                  textAlign: "center",
                  justifyContent: "center",
                  alignItems: "center",
                },
              ]}
            >
              <Text
                style={[
                  styles.cellLeft,
                  {
                    fontSize: 14,
                    fontWeight: "bold",
                    textAlign: "center",
                    justifyContent: "center",
                    alignItems: "center",
                  },
                ]}
              >
                {agent.displayName.toUpperCase()}
              </Text>
            </View>
          </View>

          <View
            style={[
              styles.pageSection,
              {
                marginTop: 10,
                flexGrow: 0,
                marginHorizontal: 20,
                borderWidth: 2,
                borderColor: "#000",
                borderStyle: "solid",
              },
            ]}
          >
            <View
              style={[
                styles.pageViewLeft,
                {
                  width: "50%",
                  padding: 0,
                  flexGrow: 0,
                  backgroundColor: "#E0E0E0",
                  lineHeight: 1,
                },
              ]}
            >
              <Text
                style={[
                  styles.cellLeft,
                  { fontSize: 10, textAlign: "center", fontWeight: "bold" },
                ]}
              >{`የግል ስራና ሰራተኛ አገናኝ ኤጀንሲ ፊርማ`}</Text>
              <Text
                style={[
                  styles.cellCenter,
                  { fontSize: 10, textAlign: "center", fontWeight: "bold" },
                ]}
              >{`Private Employment Agency Signature`}</Text>
              <Text
                style={[
                  styles.cellRight,
                  {
                    fontSize: 10,
                    textAlign: "center",
                    fontWeight: "bold",
                    paddingBottom: 5,
                  },
                ]}
              >{`توقيع وكالة التوظيف الخاصة`}</Text>
              <View
                style={{
                  width: "100%",
                  height: 40,
                  borderTopWidth: 2,
                  backgroundColor: "#fff",
                  borderColor: "#000",
                  borderStyle: "solid",
                }}
              ></View>
            </View>
            <View
              style={[
                styles.pageViewLeft,
                {
                  width: "50%",
                  padding: 0,
                  flexGrow: 0,
                  backgroundColor: "#E0E0E0",
                  lineHeight: 1,
                },
              ]}
            >
              <Text
                style={[
                  styles.cellLeft,
                  { fontSize: 10, textAlign: "center", fontWeight: "bold" },
                ]}
              >{`የሰራተኛው ፊርማ`}</Text>
              <Text
                style={[
                  styles.cellCenter,
                  { fontSize: 10, textAlign: "center", fontWeight: "bold" },
                ]}
              >{`Employee Signature`}</Text>
              <Text
                style={[
                  styles.cellRight,
                  {
                    fontSize: 10,
                    textAlign: "center",
                    fontWeight: "bold",
                    paddingBottom: 5,
                  },
                ]}
              >{`توقيع الموظف`}</Text>
              <View
                style={{
                  width: "100%",
                  height: 40,
                  borderTopWidth: 2,
                  backgroundColor: "#fff",
                  borderColor: "#000",
                  borderStyle: "solid",
                }}
              ></View>
            </View>
          </View>
          <View
            style={[
              styles.pageSection,
              {
                flexGrow: 0,
                marginHorizontal: 20,
                borderWidth: 2,
                borderColor: "#000",
                borderStyle: "solid",
              },
            ]}
          >
            <View
              style={[
                styles.pageViewLeft,
                {
                  width: "50%",
                  padding: 0,
                  flexGrow: 0,
                  backgroundColor: "#E0E0E0",
                  lineHeight: 1,
                },
              ]}
            >
              <Text
                style={[
                  styles.cellLeft,
                  { fontSize: 10, textAlign: "center", fontWeight: "bold" },
                ]}
              >{`የኤጀንሲው ተወካይ ፊርማ`}</Text>
              <Text
                style={[
                  styles.cellCenter,
                  { fontSize: 10, textAlign: "center", fontWeight: "bold" },
                ]}
              >{`Agency Representative Signature`}</Text>
              <Text
                style={[
                  styles.cellRight,
                  {
                    fontSize: 10,
                    textAlign: "center",
                    fontWeight: "bold",
                    paddingBottom: 5,
                  },
                ]}
              >{`توقيع ممثل الوكالة`}</Text>
              <View
                style={{
                  width: "100%",
                  height: 40,
                  borderTopWidth: 2,
                  backgroundColor: "#fff",
                  borderColor: "#000",
                  borderStyle: "solid",
                }}
              ></View>
            </View>
            <View
              style={[
                styles.pageViewLeft,
                {
                  width: "50%",
                  padding: 0,
                  flexGrow: 0,
                  backgroundColor: "#E0E0E0",
                  lineHeight: 1,
                },
              ]}
            >
              <Text
                style={[
                  styles.cellLeft,
                  { fontSize: 10, textAlign: "center", fontWeight: "bold" },
                ]}
              >{`የአሰሪው ፊርማ`}</Text>
              <Text
                style={[
                  styles.cellCenter,
                  { fontSize: 10, textAlign: "center", fontWeight: "bold" },
                ]}
              >{`Employer Signature`}</Text>
              <Text
                style={[
                  styles.cellRight,
                  {
                    fontSize: 10,
                    textAlign: "center",
                    fontWeight: "bold",
                    paddingBottom: 5,
                  },
                ]}
              >{`توقيع الموظف`}</Text>
              <View
                style={{
                  width: "100%",
                  height: 40,
                  borderTopWidth: 2,
                  backgroundColor: "#fff",
                  borderColor: "#000",
                  borderStyle: "solid",
                }}
              ></View>
            </View>
          </View>

          <View
            style={[
              styles.pageSection,
              {
                marginTop: 40,
                lineHeight: 2,
                flexGrow: 0,
                marginHorizontal: 10,
                borderWidth: 2,
                borderColor: "#000",
                borderStyle: "solid",
              },
            ]}
          >
            <View style={[styles.pageViewLeft, { width: "25%", padding: 0.5 }]}>
              <View style={[styles.row3]} key="44">
                <View
                  style={{
                    backgroundColor: "#E0E0E0",
                    paddingBottom: 2,
                  }}
                  key="30"
                >
                  <Text
                    style={[
                      styles.cellLeft,
                      { color: "#000", fontWeight: "bold" },
                    ]}
                  >{`ምስክሮች`}</Text>
                </View>

                <Text style={[styles.cellLeft, { paddingLeft: 2 }]}>
                  {`1. ሙሉ ስም: `}
                </Text>
                <Text style={[styles.cellLeft, { paddingLeft: 2 }]}>
                  {`አድራሻ: `}
                </Text>
                <Text style={[styles.cellLeft, { paddingLeft: 2 }]}>
                  {`ስልክ ቁጥር: `}
                </Text>
                <Text
                  style={[styles.cellLeft, { paddingLeft: 2 }]}
                >{`ፊርማ: `}</Text>
                <Text style={[styles.cellLeft, { paddingLeft: 2 }]}>
                  {`2. ሙሉ ስም: `}
                </Text>
                <Text style={[styles.cellLeft, { paddingLeft: 2 }]}>
                  {`አድራሻ: `}
                </Text>
                <Text style={[styles.cellLeft, { paddingLeft: 2 }]}>
                  {`ስልክ ቁጥር: `}
                </Text>
                <Text
                  style={[styles.cellLeft, { paddingLeft: 2 }]}
                >{`ፊርማ: `}</Text>
              </View>
            </View>
            <View
              style={[styles.pageViewCenter, { width: "50%", padding: 0.5 }]}
            >
              <View
                style={[
                  styles.row3,
                  { paddingBottom: 10, fontWeight: "bold", lineHeight: 2 },
                ]}
                key="44"
              >
                <View
                  style={{
                    backgroundColor: "#E0E0E0",
                    paddingBottom: 2,
                  }}
                  key="30"
                >
                  <Text
                    style={[
                      styles.cellCenter,
                      { color: "#000", fontWeight: "bold" },
                    ]}
                  >{`Witnesses`}</Text>
                </View>

                <View style={[styles.row33, { paddingRight: 10 }]}>
                  <Text style={styles.cellCenter}>{`1. Full Name: `}</Text>
                  <Text style={styles.cellCenterBold}>
                    {`${employee.firstTestimonialName || ""}`}
                  </Text>
                </View>
                <View style={[styles.row33, { paddingHorizontal: 10 }]}>
                  <Text style={styles.cellCenter}>{`Address: `}</Text>
                  <Text style={styles.cellCenterBold}>
                    {`${employee.firstTestimonialAddress || ""}`}
                  </Text>
                </View>
                <View style={[styles.row33, { paddingHorizontal: 10 }]}>
                  <Text style={styles.cellCenter}>{`Phone Number: `}</Text>
                  <Text style={styles.cellCenterBold}>
                    {`${employee.firstTestimonialTelephone || ""}`}
                  </Text>
                </View>
                <View style={[styles.row33, { paddingHorizontal: 10 }]}>
                  <Text style={styles.cellCenter}>{`Signature: `}</Text>
                  <Text
                    style={[
                      styles.cellLeft,
                      { borderBottom: "1px solid #000", flexGrow: 1 },
                    ]}
                  >{` `}</Text>
                </View>
                <View style={[styles.row33, { paddingRight: 10 }]}>
                  <Text style={styles.cellCenter}>{`2. Full Name: `}</Text>
                  <Text style={styles.cellCenterBold}>
                    {`${employee.secondTestimonialName || ""}`}
                  </Text>
                </View>
                <View style={[styles.row33, { paddingHorizontal: 10 }]}>
                  <Text style={styles.cellCenter}>{`Address: `}</Text>
                  <Text style={styles.cellCenterBold}>
                    {`${employee.secondTestimonialAddress || ""}`}
                  </Text>
                </View>
                <View style={[styles.row33, { paddingHorizontal: 10 }]}>
                  <Text style={styles.cellCenter}>{`Phone Number: `}</Text>
                  <Text style={styles.cellCenterBold}>
                    {`${employee.secondTestimonialTelephone || ""}`}
                  </Text>
                </View>
                <View style={[styles.row33, { paddingHorizontal: 10 }]}>
                  <Text style={styles.cellCenter}>{`Signature: `}</Text>
                  <Text
                    style={[
                      styles.cellLeft,
                      { borderBottom: "1px solid #000", flexGrow: 1 },
                    ]}
                  >{` `}</Text>
                </View>
              </View>
            </View>
            <View
              style={[styles.pageViewRight, { width: "25%", padding: 0.5 }]}
            >
              <View style={[styles.row3]} key="44">
                <View
                  style={{
                    backgroundColor: "#E0E0E0",
                    paddingBottom: 2,
                  }}
                  key="30"
                >
                  <Text
                    style={[
                      styles.cellRight,
                      { color: "#000", fontWeight: "bold" },
                    ]}
                  >{`الشهود`}</Text>
                </View>

                <Text style={[styles.cellRight, { paddingRight: 2 }]}>
                  {`1. الاسم الكامل`}
                </Text>
                <Text style={[styles.cellRight, { paddingRight: 2 }]}>
                  {`العنوان`}
                </Text>
                <Text style={[styles.cellRight, { paddingRight: 2 }]}>
                  {`رقم الهاتف`}
                </Text>
                <Text
                  style={[styles.cellRight, { paddingRight: 2 }]}
                >{`التوقيع`}</Text>

                <Text style={[styles.cellRight, { paddingRight: 2 }]}>
                  {`2. الاسم الكامل`}
                </Text>
                <Text style={[styles.cellRight, { paddingRight: 2 }]}>
                  {`العنوان`}
                </Text>
                <Text style={[styles.cellRight, { paddingRight: 2 }]}>
                  {`رقم الهاتف`}
                </Text>
                <Text
                  style={[styles.cellRight, { paddingRight: 2 }]}
                >{`التوقيع`}</Text>
              </View>
            </View>
          </View>

          <View style={{ borderTop: "1px solid #000" }} fixed={true}></View>
        </Page>
      </Document>
    );
  } else {
    return (
      <Document
        creator="pinnasofts"
        author="pinnaSofts"
        title="EnjazPlus"
        subject="Contract Translation"
      >
        <Page size="LETTER" style={styles.page}>
          <View
            style={{
              textAlign: "center",
              textTransform: "uppercase",
              fontSize: "20px",
              marginTop: 30,
            }}
          >
            <Text>{`Please fill all the required Data `}</Text>
          </View>
        </Page>
      </Document>
    );
  }
};
